import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
var AdvisorSection = lazy(function(){return import("../components/AdvisorSection");});
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { 
  Building2, 
  Calendar,
  ChevronRight,
  Trash2,
  Unlink,
  Loader2,
  AlertTriangle,
  Bell,
  GraduationCap,
  CreditCard,
  Crown,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  Users,
  Plus,
  RefreshCw,
  Pencil,
  Check,
  X,
  Ticket,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { HouseholdSettings } from "../components/HouseholdSettings";

export const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user, logout, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Reminder settings
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState("09:00");
  const [reminderLoading, setReminderLoading] = useState(true);
  const [reminderSaving, setReminderSaving] = useState(false);

  // Cash flow alert settings
  const [cashflowAlertsEnabled, setCashflowAlertsEnabled] = useState(true);
  const [cashflowAlertsLoading, setCashflowAlertsLoading] = useState(true);
  const [cashflowAlertsSaving, setCashflowAlertsSaving] = useState(false);

  // Subscription settings
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [familyLoading, setFamilyLoading] = useState(false);

  // Email preferences
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);

  // Plaid Link for adding more banks
  const [linkToken, setLinkToken] = useState(null);
  const [addingBank, setAddingBank] = useState(false);

  // Sync eligibility
  const [syncEligibility, setSyncEligibility] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Profile editing
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Plaid connection status
  const [plaidStatus, setPlaidStatus] = useState(null);

  // Coupon code
  const [couponCode, setCouponCode] = useState("");
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);  // null, { valid: true, ... }, or { valid: false, error: "..." }

  const fetchSyncEligibility = useCallback(async () => {
    try {
      const response = await api.get("/plaid/sync-eligibility");
      setSyncEligibility(response.data);
    } catch (error) {
      console.error("Failed to fetch sync eligibility:", error);
    }
  }, [api]);

  const fetchPlaidStatus = useCallback(async () => {
    try {
      const response = await api.get("/plaid/status");
      setPlaidStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Plaid status:", error);
    }
  }, [api]);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await api.post("/plaid/sync");
      toast.success("Sync completed successfully");
      fetchAccounts();
      fetchSyncEligibility();
    } catch (error) {
      const message = error.response?.data?.detail || "Sync failed";
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchSubscriptionStatus = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const response = await api.get("/subscription/status");
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [api]);

  // Check for subscription success/cancel from URL params
  useEffect(() => {
    const subscriptionStatus = searchParams.get("subscription");
    const sessionId = searchParams.get("session_id");
    
    if (subscriptionStatus === "success" && sessionId) {
      const plan = searchParams.get("plan");
      const pollStatus = async () => {
        try {
          const response = await api.get(`/subscription/checkout-status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            if (plan === "family_seat") {
              toast.success("A seat was added to your household!");
            } else if (plan === "family") {
              toast.success("Welcome to Tally Family! Your household plan is active.");
            } else {
              toast.success("Welcome to Tally Power! Your 7-day trial has started.");
            }
            fetchSubscriptionStatus();
          }
        } catch (error) {
          console.error("Failed to check payment status:", error);
        }
      };
      pollStatus();
      // Clear URL params
      navigate("/settings", { replace: true });
    } else if (subscriptionStatus === "cancelled") {
      toast.info("Subscription checkout was cancelled.");
      navigate("/settings", { replace: true });
    }
  }, [searchParams, api, navigate, fetchSubscriptionStatus]);

  const fetchReminderSettings = useCallback(async () => {
    try {
      const response = await api.get("/user/reminder-settings");
      setReminderEnabled(response.data.enabled);
      setReminderTime(response.data.reminder_time || "09:00");
    } catch (error) {
      console.error("Failed to fetch reminder settings:", error);
    } finally {
      setReminderLoading(false);
    }
  }, [api]);

  const fetchCashflowAlertSettings = useCallback(async () => {
    try {
      const response = await api.get("/user/cashflow-alert-settings");
      setCashflowAlertsEnabled(response.data.enabled);
    } catch (error) {
      console.error("Failed to fetch cashflow alert settings:", error);
    } finally {
      setCashflowAlertsLoading(false);
    }
  }, [api]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch Plaid link token for adding new banks
  const fetchLinkToken = useCallback(async () => {
    try {
      const response = await api.post("/plaid/link-token");
      setLinkToken(response.data.link_token);
    } catch (error) {
      console.error("Failed to fetch link token:", error);
    }
  }, [api]);

  // Handle successful Plaid connection
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setAddingBank(true);
    try {
      const response = await api.post("/plaid/exchange_public_token", {
        public_token: publicToken,
        metadata: metadata
      });
      
      const accountsCount = response.data?.accounts_connected || 0;
      const institutionName = metadata?.institution?.name || 'your bank';
      
      if (accountsCount > 0) {
        toast.success(`Connected ${accountsCount} account${accountsCount !== 1 ? 's' : ''} from ${institutionName}!`);
      } else {
        toast.info(`Connected to ${institutionName}. Your accounts will sync shortly.`);
      }
      
      // Refresh accounts list
      fetchAccounts();
    } catch (error) {
      console.error("Failed to exchange token:", error);
      const errorMessage = error.response?.data?.detail || "Failed to connect bank. Please try again.";
      toast.error(errorMessage);
    } finally {
      setAddingBank(false);
    }
  }, [api, fetchAccounts]);

  // Plaid Link configuration
  const plaidConfig = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (error) => {
      if (error) {
        console.log("Plaid Link error:", error);
      }
    },
  };

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink(plaidConfig);

  // Handle adding a new bank
  const handleAddBank = () => {
    if (linkToken && plaidReady) {
      openPlaidLink();
    } else {
      toast.error("Please wait, connecting to Plaid...");
      fetchLinkToken();
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchReminderSettings();
    fetchCashflowAlertSettings();
    fetchSubscriptionStatus();
    fetchLinkToken();
    fetchSyncEligibility();
    fetchPlaidStatus();
  }, [fetchAccounts, fetchReminderSettings, fetchCashflowAlertSettings, fetchSubscriptionStatus, fetchLinkToken, fetchSyncEligibility, fetchPlaidStatus]);

  const handleCashflowAlertToggle = async (enabled) => {
    setCashflowAlertsSaving(true);
    try {
      await api.put("/user/cashflow-alert-settings", { enabled });
      setCashflowAlertsEnabled(enabled);
      toast.success(enabled ? "Cash flow alerts enabled" : "Cash flow alerts disabled");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setCashflowAlertsSaving(false);
    }
  };

  const fetchEmailPrefs = useCallback(async () => {
    try {
      const res = await api.get("/email/preferences/me");
      setEmailPrefs(res.data);
    } catch (e) {
      // non-blocking
    }
  }, [api]);

  useEffect(() => { fetchEmailPrefs(); }, [fetchEmailPrefs]);

  const toggleEmailPref = async (key, value) => {
    const current = emailPrefs?.categories || {};
    const next = { tips: current.tips, product: current.product, [key]: value };
    setEmailPrefs((p) => ({ ...p, categories: { ...p.categories, [key]: value } }));
    setEmailPrefsSaving(true);
    try {
      await api.put("/email/preferences/me", { tips: !!next.tips, product: !!next.product });
      toast.success("Email preferences updated");
    } catch (e) {
      toast.error("Failed to update preferences");
      fetchEmailPrefs();
    } finally {
      setEmailPrefsSaving(false);
    }
  };


  const handleStartTrial = async (billingPeriod = "monthly") => {
    setStartingTrial(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        billing_period: billingPeriod
      };
      // Include coupon code if valid
      if (couponValid?.valid && couponCode) {
        payload.coupon_code = couponCode.toUpperCase();
      }
      const response = await api.post("/subscription/create-checkout", payload);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Failed to start trial:", error);
      toast.error(error.response?.data?.detail || "Failed to start trial");
      setStartingTrial(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValid(null);
      return;
    }
    setCouponValidating(true);
    try {
      const response = await api.post("/coupons/validate", { code: couponCode.toUpperCase() });
      setCouponValid(response.data);
      toast.success(`Coupon applied: ${response.data.discount_preview}`);
    } catch (error) {
      setCouponValid({ valid: false, error: error.response?.data?.detail || "Invalid coupon" });
      toast.error(error.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponValidating(false);
    }
  };

  const handleClearCoupon = () => {
    setCouponCode("");
    setCouponValid(null);
  };

  const handleFamilyUpgrade = async () => {
    setFamilyLoading(true);
    try {
      const response = await api.post("/subscription/family/create-checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Failed to start Family checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setFamilyLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You will retain access until the end of your billing period.");
      fetchSubscriptionStatus();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const formatTrialEnd = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`;
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await api.put("/user/profile", { name: newName.trim() });
      updateUser({ name: newName.trim() });
      setEditingName(false);
      toast.success("Name updated");
    } catch (error) {
      toast.error("Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleDisconnectBank = async () => {
    setDisconnecting(true);
    try {
      await api.delete("/plaid/disconnect");
      toast.success("Bank accounts disconnected");
      navigate("/onboarding");
    } catch (error) {
      toast.error("Failed to disconnect bank");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDeleteData = async () => {
    setDeleting(true);
    try {
      await api.delete("/user/data");
      toast.success("All data deleted");
      logout();
      navigate("/login");
    } catch (error) {
      toast.error("Failed to delete data");
    } finally {
      setDeleting(false);
    }
  };

  const handleReminderToggle = async (enabled) => {
    setReminderSaving(true);
    setReminderEnabled(enabled);
    try {
      await api.put("/user/reminder-settings", {
        enabled,
        reminder_time: reminderTime
      });
      toast.success(enabled ? "Daily reminders enabled" : "Daily reminders disabled");
    } catch (error) {
      setReminderEnabled(!enabled); // Revert on error
      toast.error("Failed to update reminder settings");
    } finally {
      setReminderSaving(false);
    }
  };

  const handleReminderTimeChange = async (time) => {
    setReminderTime(time);
    if (reminderEnabled) {
      try {
        await api.put("/user/reminder-settings", {
          enabled: reminderEnabled,
          reminder_time: time
        });
        toast.success("Reminder time updated");
      } catch (error) {
        toast.error("Failed to update reminder time");
      }
    }
  };

  return (
    <div data-testid="settings-page" style={{maxWidth:720}}>
      <div className="phead"><div><h1>Settings</h1><div className="sub">Manage your account and preferences</div></div></div>

      {/* Profile */}
      <div className="lab" style={{margin:"6px 2px 12px"}}>Profile</div>
      <div className="card setrow" style={{marginBottom:28}}>
        <div className="avatar" style={{width:46,height:46,borderRadius:"50%",background:"rgba(126,217,87,.12)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,color:"#14432B",fontSize:16,flexShrink:0}}>
          {(newName || user?.name)?.charAt(0)?.toUpperCase() || "U"}{(newName || user?.name)?.split(" ")[1]?.charAt(0)?.toUpperCase() || ""}
        </div>
        <div className="nm" style={{flex:1,minWidth:0}}>
          {editingName ? (
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} style={{height:38,padding:"0 12px",border:"1.5px solid #ECE8DC",borderRadius:10,fontSize:15,fontFamily:"inherit",outline:"none"}} autoFocus data-testid="edit-name-input" />
              <button onClick={handleSaveName} disabled={savingName} style={{background:"none",border:"none",cursor:"pointer",color:"#3F7D52"}} data-testid="save-name-btn">{savingName ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}</button>
              <button onClick={() => { setEditingName(false); setNewName(user?.name || ""); }} style={{background:"none",border:"none",cursor:"pointer",color:"#7d7d70"}} data-testid="cancel-edit-name-btn"><X size={16} /></button>
            </div>
          ) : (
            <b style={{fontSize:16}}>{user?.name}</b>
          )}
          <small style={{display:"block",fontSize:13,color:"#7d7d70"}}>{user?.email}</small>
        </div>
        {!editingName && <button className="btn btn-g" style={{padding:"9px 15px"}} onClick={() => { setNewName(user?.name || ""); setEditingName(true); }} data-testid="edit-name-btn"><Pencil size={15} /> Edit</button>}
      </div>

      {/* Subscription */}
      <div className="lab" style={{margin:"6px 2px 12px"}}>Subscription</div>
      <section>
        <Card className="border-border/50 overflow-hidden">
          {subscriptionLoading ? (
            <CardContent className="p-6">
              <Skeleton className="h-24 rounded-xl" />
            </CardContent>
          ) : (
            <CardContent className="p-6">
              {/* Current Plan Status */}
              {subscription?.has_subscription ? (
                <div className="space-y-4">
                  {/* Active/Trial Badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg">Tally Power</span>
                          {subscription.status === "trialing" && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              <Clock className="w-3 h-3 mr-1" />
                              Trial
                            </Badge>
                          )}
                          {(subscription.status === "active" || subscription.status === "comp") && (
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {subscription.status === "comp" ? "Complimentary" : "Active"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {subscription.status === "trialing" 
                            ? `Free trial • ${formatTrialEnd(subscription.trial_ends_at)}`
                            : subscription.status === "comp"
                            ? "Complimentary access"
                            : "$14.99 CAD/month"
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Your Power benefits
                    </p>
                    <div className="grid gap-1.5 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span>AI-powered financial insights</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span>Unlimited bank connections</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span>All calendar integrations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span>Priority support</span>
                      </div>
                    </div>
                  </div>

                  {/* Cancel Button */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full text-muted-foreground hover:text-destructive hover:border-destructive rounded-full"
                        data-testid="cancel-subscription-btn"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Cancel subscription
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                          <p>
                            {subscription.status === "trialing" 
                              ? "Your trial will end immediately and you won't be charged."
                              : "You'll retain access until the end of your current billing period."
                            }
                          </p>
                          <p className="font-medium text-foreground">
                            You&apos;ll lose access to:
                          </p>
                          <ul className="list-disc list-inside text-sm space-y-1">
                            <li>AI-powered financial insights</li>
                            <li>Advanced budget features</li>
                            <li>Google & Microsoft calendar sync</li>
                            <li>Priority customer support</li>
                          </ul>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Keep subscription</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleCancelSubscription}
                          disabled={cancellingSubscription}
                          className="bg-destructive hover:bg-destructive/90 rounded-full"
                        >
                          {cancellingSubscription ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Cancelling...
                            </>
                          ) : (
                            "Yes, cancel subscription"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : subscription?.status === "cancelled" ? (
                // Cancelled state
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Crown className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg">Subscription Cancelled</span>
                        <Badge variant="secondary" className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                          Cancelled
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your access will continue until the billing period ends
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleStartTrial}
                    disabled={startingTrial}
                    className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    data-testid="resubscribe-btn"
                  >
                    {startingTrial ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Crown className="mr-2 h-4 w-4" />
                        Resubscribe to Power
                      </>
                    )}
                  </Button>
                </div>
              ) : subscription?.show_upgrade_prompt || subscription?.status === "none" || subscription?.status === "expired" ? (
                // Trial expired or no subscription - Show upgrade prompt
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Crown className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <span className="font-semibold text-lg">
                        {subscription?.status === "expired" ? "Trial Expired" : "Free Plan"}
                      </span>
                      <p className="text-sm text-muted-foreground">
                        Upgrade to unlock all features
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      <span className="font-semibold text-amber-900 dark:text-amber-100">Upgrade to Tally Power</span>
                    </div>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                      Get AI insights, unlimited banks, all calendars, and more.
                    </p>

                    {/* Coupon Code Input */}
                    <div className="mb-4">
                      <Label className="text-sm text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1.5">
                        <Ticket className="w-4 h-4" />
                        Have a coupon code?
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase());
                            setCouponValid(null);
                          }}
                          className="flex-1 uppercase bg-white dark:bg-gray-900"
                          disabled={couponValid?.valid}
                          data-testid="coupon-code-input"
                        />
                        {couponValid?.valid ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleClearCoupon}
                            className="border-amber-300 dark:border-amber-700"
                            data-testid="clear-coupon-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleValidateCoupon}
                            disabled={couponValidating || !couponCode.trim()}
                            className="border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                            data-testid="apply-coupon-btn"
                          >
                            {couponValidating ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Apply"
                            )}
                          </Button>
                        )}
                      </div>
                      {couponValid?.valid && (
                        <div className="mt-2 p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg border border-emerald-300 dark:border-emerald-700">
                          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm font-medium">{couponValid.discount_preview}</span>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400">• {couponValid.duration}</span>
                          </div>
                        </div>
                      )}
                      {couponValid && !couponValid.valid && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{couponValid.error}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Button 
                        onClick={() => handleStartTrial("monthly")}
                        disabled={startingTrial}
                        className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                        data-testid="upgrade-monthly-btn"
                      >
                        {startingTrial ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading checkout...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-4 w-4" />
                            Upgrade — $14.99/month
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={() => handleStartTrial("yearly")}
                        disabled={startingTrial}
                        variant="outline"
                        className="w-full rounded-full border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        data-testid="upgrade-yearly-btn"
                      >
                        {startingTrial ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Crown className="mr-2 h-4 w-4 text-amber-600" />
                            Upgrade — $149.99/year (save $30)
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 text-center">
                      You can also enter codes at Stripe checkout
                    </p>
                  </div>
                </div>
              ) : subscription?.is_free_tier ? (
                // Free tier (student/senior)
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <span className="font-semibold text-lg">
                      {user?.plan === "student" ? "Tally Student" : "Tally Senior"}
                    </span>
                    <p className="text-sm text-muted-foreground">
                      Free forever • Full access included
                    </p>
                  </div>
                </div>
              ) : (
                // Default fallback
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                    <Crown className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <span className="font-semibold text-lg">Free Plan</span>
                    <p className="text-sm text-muted-foreground">
                      Upgrade to unlock all features
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </section>

      {/* Email preferences */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Email preferences</div>
        <Card className="border-border/50" data-testid="email-prefs-card">
          <CardContent className="p-6 space-y-1">
            <p className="text-sm text-muted-foreground mb-3">Choose which emails you'd like to receive. Account &amp; security emails are always sent.</p>
            {[
              { key: "tips", title: "Tips & Insights", desc: "Budgeting tips, insights and how-to guides" },
              { key: "product", title: "Product & Offers", desc: "New features, updates and occasional offers" },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-4 py-3 border-b border-border/40 last:border-0" data-testid={`email-pref-${row.key}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{row.title}</p>
                  <p className="text-xs text-muted-foreground">{row.desc}</p>
                </div>
                <Switch
                  checked={!!emailPrefs?.categories?.[row.key]}
                  disabled={emailPrefsSaving || !emailPrefs}
                  onCheckedChange={(v) => toggleEmailPref(row.key, v)}
                  data-testid={`email-pref-switch-${row.key}`}
                />
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 py-3" data-testid="email-pref-security">
              <div className="min-w-0">
                <p className="text-sm font-medium">Account &amp; Security</p>
                <p className="text-xs text-muted-foreground">Account, billing and security notices (required)</p>
              </div>
              <Switch checked disabled data-testid="email-pref-switch-security" />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Tally Family upgrade */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Household</div>
        <Card className="border-border/50" data-testid="family-card">
          <CardContent className="p-6">
            {(subscription?.plan === "family" && (subscription?.status === "active" || subscription?.status === "comp")) ? (
              <div className="flex items-center gap-3" data-testid="family-active">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">Tally Family</span>
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Active
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">$149 CAD/year • Household premium</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-lg">Tally Family</span>
                    <p className="text-sm text-muted-foreground">Bring your whole household onto Tally</p>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                  <div className="grid gap-1.5 text-sm text-muted-foreground sm:grid-cols-2">
                    {["Partner accounts", "Shared budgets & goals", "Household dashboard", "Shared document vault"].map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-violet-500" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleFamilyUpgrade}
                  disabled={familyLoading}
                  className="w-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                  data-testid="upgrade-family-btn"
                >
                  {familyLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading checkout...</>
                  ) : (
                    <><Users className="mr-2 h-4 w-4" />Upgrade to Family — $149/year</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Calendar section */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Calendar</div>
        <Card className="border-border/50">
          <CardContent className="p-6">
            <Button
              variant="outline"
              className="w-full justify-between min-h-[52px] rounded-xl"
              onClick={() => navigate("/settings/calendar")}
              data-testid="calendar-settings-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="font-medium">Calendar connections</span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Learning Reminders section */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Learning Reminders</div>
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-5">
            {reminderLoading ? (
              <Skeleton className="h-12 rounded-xl" />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Bell className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Daily lesson reminders</p>
                      <p className="text-sm text-muted-foreground">
                        Get reminded to continue your financial education
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={reminderEnabled}
                    onCheckedChange={handleReminderToggle}
                    disabled={reminderSaving}
                    data-testid="reminder-toggle"
                  />
                </div>
                
                {reminderEnabled && (
                  <div className="pl-[52px] space-y-3">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Reminder time
                      </Label>
                      <Select value={reminderTime} onValueChange={handleReminderTimeChange}>
                        <SelectTrigger className="w-[140px]" data-testid="reminder-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="07:00">7:00 AM</SelectItem>
                          <SelectItem value="08:00">8:00 AM</SelectItem>
                          <SelectItem value="09:00">9:00 AM</SelectItem>
                          <SelectItem value="10:00">10:00 AM</SelectItem>
                          <SelectItem value="12:00">12:00 PM</SelectItem>
                          <SelectItem value="18:00">6:00 PM</SelectItem>
                          <SelectItem value="20:00">8:00 PM</SelectItem>
                          <SelectItem value="21:00">9:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 text-sm text-muted-foreground">
                      <GraduationCap className="w-4 h-4 shrink-0" />
                      <span>You&apos;ll see a reminder when you open Tally if you have lessons to complete.</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Cash Flow Alerts section */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Cash Flow Alerts</div>
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-5">
            {cashflowAlertsLoading ? (
              <Skeleton className="h-12 rounded-xl" />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                    </div>
                    <div>
                      <p className="font-medium">Shortfall email alerts</p>
                      <p className="text-sm text-muted-foreground">
                        Get notified 5 days before your balance goes negative
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={cashflowAlertsEnabled}
                    onCheckedChange={handleCashflowAlertToggle}
                    disabled={cashflowAlertsSaving}
                    data-testid="cashflow-alert-toggle"
                  />
                </div>
                
                {cashflowAlertsEnabled && (
                  <div className="pl-[52px]">
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 text-sm text-muted-foreground">
                      <Bell className="w-4 h-4 shrink-0" />
                      <span>We check daily at noon EST and email you if bills in the next 5 days will overdraw your checking account.</span>
                    </div>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/cash-flow")}
                  className="gap-2"
                  data-testid="view-forecast-btn"
                >
                  View 90-day forecast
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Household section */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}><span style={{display:"flex",alignItems:"center",gap:8}}>
          <Users className="w-4 h-4 text-primary" />
          Household
        </span></div>
        <HouseholdSettings />
      </section>

      {/* Connected accounts section */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}>Connected Accounts</div>
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-4">
            {/* Re-authentication required banner */}
            {plaidStatus?.requires_reauth && accounts.length > 0 && (
              <div className="p-4 rounded-xl border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 space-y-3" data-testid="reauth-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Bank connection expired</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Your bank requires re-authentication. Your transactions won't sync until you reconnect.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleAddBank}
                  disabled={addingBank || !plaidReady}
                  className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="reconnect-bank-btn"
                >
                  {addingBank ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Reconnecting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reconnect Your Bank
                    </>
                  )}
                </Button>
              </div>
            )}

            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 rounded-xl" />
                <Skeleton className="h-16 rounded-xl" />
              </div>
            ) : accounts.length === 0 ? (
              <div className="text-center py-6">
                <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
                <p className="text-muted-foreground mb-4">No accounts connected</p>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => navigate("/onboarding")}
                >
                  Connect bank
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <div 
                      key={account.id}
                      className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        plaidStatus?.requires_reauth 
                          ? "border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/10" 
                          : "border-border/50 bg-muted/30 hover:bg-muted/50"
                      }`}
                      data-testid={`account-${account.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          plaidStatus?.requires_reauth 
                            ? "bg-amber-100 dark:bg-amber-900/30" 
                            : "bg-primary/10"
                        }`}>
                          <Building2 className={`w-5 h-5 ${
                            plaidStatus?.requires_reauth 
                              ? "text-amber-600 dark:text-amber-500" 
                              : "text-primary"
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium">{account.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {account.institution_name} •••{account.mask}
                          </p>
                        </div>
                      </div>
                      {plaidStatus?.requires_reauth ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-500">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Needs reconnect
                        </span>
                      ) : (
                        <span className="status-dot status-connected" title="Connected" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Add another bank button */}
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={handleAddBank}
                  disabled={addingBank || !plaidReady}
                  data-testid="add-bank-btn"
                >
                  {addingBank ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add another bank
                    </>
                  )}
                </Button>

                {/* Manual Sync Button - only for Power plan */}
                {syncEligibility && syncEligibility.plan === "power" && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={handleManualSync}
                      disabled={syncing || !syncEligibility.can_sync}
                      data-testid="sync-btn"
                    >
                      {syncing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Sync Now
                        </>
                      )}
                    </Button>
                    {!syncEligibility.can_sync && syncEligibility.hours_until_next_sync > 0 && (
                      <p className="text-xs text-muted-foreground text-center">
                        Next sync available in {syncEligibility.hours_until_next_sync} hours
                      </p>
                    )}
                  </div>
                )}

                {/* Sync info for Student/Senior plans */}
                {syncEligibility && (syncEligibility.plan === "student" || syncEligibility.plan === "senior") && (
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-sm text-muted-foreground">
                      Your accounts sync automatically once per week.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Upgrade to Tally Power for daily manual syncs.
                    </p>
                  </div>
                )}

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full text-muted-foreground hover:text-destructive hover:border-destructive rounded-full"
                      data-testid="disconnect-bank-btn"
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Disconnect all accounts
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect bank accounts?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will remove all connected accounts and transaction history. You can reconnect your bank anytime.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDisconnectBank}
                        disabled={disconnecting}
                        className="bg-destructive hover:bg-destructive/90 rounded-full"
                      >
                        {disconnecting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Disconnecting...
                          </>
                        ) : (
                          "Disconnect"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Advisor Collaboration */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px"}}><span style={{display:"flex",alignItems:"center",gap:8}}>
          <Shield className="w-4 h-4" />
          Advisor Collaboration
        </span></div>
        <Card>
          <CardContent className="p-6">
            <Suspense fallback={<Skeleton className="h-20 rounded-xl" />}>
              <AdvisorSection api={api} />
            </Suspense>
          </CardContent>
        </Card>
      </section>

      {/* Danger zone */}
      <section>
        <div className="lab" style={{margin:"6px 2px 12px",color:"#B0603F"}}><span style={{display:"flex",alignItems:"center",gap:8}}>
          <AlertTriangle className="w-4 h-4" />
          Danger Zone
        </span></div>
        <Card className="border-destructive/30">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-4">
              This action is irreversible and will permanently delete all your data.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  className="w-full rounded-full"
                  data-testid="delete-data-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete all my data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete all data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your account and all associated data including transactions, budgets, and calendar connections.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteData}
                    disabled={deleting}
                    className="bg-destructive hover:bg-destructive/90 rounded-full"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      "Delete everything"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};
